import { Grid, Cell } from 'react-foundation'
import { Formik, Form, FormikHelpers, FormikProps, useFormikContext, useField } from 'formik'
import * as Yup from 'yup'
import QueryString from 'query-string'
import type { ServiceRequestService, Notification } from '../../../models'
import { LookupSelectFieldGroup, LookupCheckboxFieldGroup, DatePickerFormGroup, InputFormGroup, ErrorMessage, NearbyCountySelectFieldGroup, FocusOnFirstError } from '../../Common/Input/FormFields'
import React, { useContext, useEffect, useState } from 'react'
import { displayDate, displayDayList, sortDays } from '../../../utilities'
import StatusSelect from '../../Common/Input/StatusSelect'
import { ServiceRequestContext } from '../ServiceRequestContext'
import { navigate, useLocation } from '@reach/router'
import UserContext, { Roles } from '../../../UserContext'
import { ApiFetch, ApiPost, ApiPut } from '../../Common/Api'
import { SaveEvent, ClickEvent } from '../../Common/AnalyticsEvents'
import SubmitButton, { SubmitMessage } from '../../Common/SubmitButton'
import { SpecialtyServicesLookup } from '../SpecialtyServicesLookup'
import { DEFAULT_STATE } from '../../Preferences/stateServiceAreaConfig'


const validationScheme = Yup.object({
    specialtyServices: Yup.string().nullable().required('Required'),
    specialtyServicesCategories: Yup.string().nullable().required('Required'),
    status: Yup.string().required('Required'),
    endDate: Yup.date().typeError('Please provide a valid date').required('Required'),
    frequencyHoursPerVisit: Yup.number()
        .min(1, 'Must be between 1 and 24')
        .max(24, 'Must be between 1 and 24')
        .required('Required'),
    frequencyVisitsTimeframe: Yup.string().required('Required'),
})

type ServiceSectionProps = {
    readOnly?: boolean
    providerView?: boolean
}

const ServiceSection: React.FC<ServiceSectionProps> = ({ readOnly, providerView }) => {
    const { services, setServices, selectedService, setSelectedService, filterValues } = useContext(ServiceRequestContext)
    const [notification, setNotification] = useState<Notification>()
    const { search, pathname } = useLocation()
    const user = useContext(UserContext)
    const [compoundSubmit, setCompoundSubmit] = useState<boolean>(false)

    const isNew = !!selectedService?.serviceRequestServiceID

    useEffect(() => {
        if (!selectedService?.serviceRequestServiceID || !selectedService?.createdBy) return
        if (!(user.hasAnyRole(Roles.WaiverServiceLeader, Roles.MarketAdmin) || (user.hasAnyRole(Roles.WaiverServiceCoordinator) && user.user.userID === selectedService.createdBy))) return

        ApiFetch<Notification[]>('Notification/ByTarget', { targetObjectType: 'serviceRequestService', targetObjectID: selectedService.serviceRequestServiceID })
            .then((response) => setNotification(response.length > 0 ? response[0] : undefined))
            // eslint-disable-next-line no-console
            .catch(console.log)
    }, [user, selectedService])

    // If we're a provider viewing a service that hasn't been awarded yet, we want to track that this provider has viewed this service
        useEffect(() => {
            if (user.roles.includes(Roles.Provider) && selectedService?.response?.decision !== 'Approved' && selectedService?.serviceRequestServiceID !== undefined) {
                ApiPut<void>(`ServiceRequestServiceReadStatus/${selectedService?.serviceRequestServiceID}`)
                    // eslint-disable-next-line no-console
                    .catch(console.log)
            }
        }, [])

    const initialValues: ServiceRequestService = {
        specialtyServices: '',
        specialtyServicesCategories: '',
        alternateCounty: '',
        status: 'Draft',
        startDate: new Date(),
        endDate: undefined,
        preferredDaysList: [],
        preferredTime: undefined,
        frequencyHoursPerVisit: undefined,
        frequencyVisitsTimeframe: undefined,
        specialConsiderationsList: [],
        ...selectedService
    }

    if (selectedService) {
        if (initialValues.startDate) initialValues.startDate = new Date(initialValues.startDate)
        if (initialValues.endDate) initialValues.endDate = new Date(initialValues.endDate)
    }

    const sendNotifications = (service: ServiceRequestService, { setStatus, setFieldValue }: FormikHelpers<ServiceRequestService>): Promise<void> => {
        if (!service?.serviceRequestServiceID || !service?.createdBy) return Promise.resolve()
        SaveEvent(ClickEvent(user, pathname, 'Send_Notifications', service.serviceRequestServiceID?.toString()))

        return ApiFetch<string[]>(`serviceRequestService/Notify?serviceRequestServiceID=${service.serviceRequestServiceID || ''}`)
            .then((response) => {
                setStatus(response.length
                    ? { success: 'This Service Posting has been Updated and Posted to Providers' }
                    : { error: 'This Service Posting has been Saved however, there are no qualified providers for this request' })
                setFieldValue('status', response.length ? 'Active' : 'Draft')
                if(response.length)
                    setSelectedService({ ...selectedService })
            })
            .catch(() => {
                setStatus({ error: 'There was an error in sending notification emails' })
            })
    }

    const saveService = (service: ServiceRequestService, { setStatus }: FormikHelpers<ServiceRequestService>): Promise<ServiceRequestService> => {
        //If it's an add:
        if (!selectedService?.serviceRequestServiceID) {
            SaveEvent(ClickEvent(user, 'Service', 'Add', selectedService?.member))
            return ApiPost<ServiceRequestService>('ServiceRequestService', undefined, { ...selectedService, ...service })
                .then((result) => {
                    if (result) {
                        setStatus({ success: 'This Service Posting has been Created' })
                        setServices([result, ...services.filter((_, i) => i <= filterValues.pageSize)])
                        const q = QueryString.stringify({ ...QueryString.parse(search), id: result.serviceRequestServiceID })
                        void navigate(`service-requests?${q}`)
                    } else {
                        setStatus({ error: 'There was an issue creating this Service Posting' })
                    }
                    return result
                })
                .catch((e: Error) => {
                    setStatus({ error: e.message })
                    throw e
                })
        }
        //If it's an edit
        else {
            SaveEvent(ClickEvent(user, 'Service', 'Update', selectedService?.member))
            return ApiPut<boolean>(`ServiceRequestService/${selectedService?.serviceRequestServiceID || ''}`, undefined, { ...selectedService, ...service })
                .then((result) => {
                    if (result) {
                        setServices(services.map(s => s.serviceRequestServiceID === service.serviceRequestServiceID ? service : s))
                        setStatus({ success: 'This Service Posting has been Updated' })
                    } else {
                        setStatus({ error: 'There was an issue updating the Service Posting' })
                        throw Error('There was an issue updating the Service Posting')
                    }
                    return { ...selectedService, service }
                })
                .catch((e: Error) => {
                    setStatus({ 'error': e.message })
                    throw e
                })
        }
    }

    const handleButton = (compound: boolean, form: FormikProps<ServiceRequestService>) => () => {
        setCompoundSubmit(compound)
        void form.submitForm()
    }

    const handleSubmit = async (service: ServiceRequestService, helpers: FormikHelpers<ServiceRequestService>): Promise<void> => {
        const s = await saveService(service, helpers)
        setSelectedService(s)

        return compoundSubmit
            ? sendNotifications(s, helpers)
            : Promise.resolve()
    }

    return (
        <Formik initialValues={initialValues} validationSchema={validationScheme} onSubmit={handleSubmit}>
            {(form) => (
                <Form>
                    <h5><i className="fas fa-file-medical"></i>Service Information</h5>
                    <Grid>
                        <Cell small={12} large={5}>
                            <StatusSelect name='status' title='Status' disabled={readOnly} readableSeperator={' - '} />
                        </Cell>
                        <Cell small={4} large={3}>
                            <DatePickerFormGroup name="startDate" title='Posting Start Date' disabled={true} />
                        </Cell>
                        <Cell small={4} large={3}>
                            <DatePickerFormGroup name="endDate" title='Posting End Date' disabled={readOnly} />
                        </Cell>
                        { !isNew ? //the value of isNew seems to be the opposite of what it should be..
                            <Cell small={12} large={5}>
                                <LookupSelectFieldGroup
                                    name='specialty'
                                    title='Specialty'
                                    lookupKey="SpecialtyOptions"
                                    includeBlank={false}
                                    disabled={readOnly}
                                /> 
                            </Cell> 
                            : null 
                        }
                        <Cell small={12} large={5}>
                            <LookupSelectFieldGroup
                                name='specialtyServicesCategories'
                                title='Service Category'
                                lookupKey="SpecialtyServicesCategories"
                                includeBlank={false}
                                disabled={readOnly}
                            />
                        </Cell>
                        <Cell small={12} large={7} className="specialtyServicesContainer">            
                            <SpecialtyServicesLookup disabled={readOnly} />
                        </Cell>
                        { providerView 
                            ?
                            <Cell small={12} large={12}>
                                <InputFormGroup
                                    name="member.plan"
                                    title={'Plan'}
                                    disabled={true}
                                />
                            </Cell>
                        : null}
                        <Grid style={{ width: '100%' }}>
                            <Cell small={12} large={6}>
                                { providerView
                                    ?
                                    <InputFormGroup
                                        name="member.zipCode"
                                        title={'Zip Code'}
                                        disabled={true}
                                    />
                                    :
                                    <NearbyCountySelectFieldGroup
                                        name="alternateCounty"
                                        stateAbbr={selectedService?.member?.state || DEFAULT_STATE}
                                        county={selectedService?.member?.county}
                                        title={'Alternate County'}
                                        includeBlank={true}
                                        disabled={readOnly}/>
                                }
                            </Cell>
                        </Grid>
                        <Cell small={6} large={6}>
                            <LookupSelectFieldGroup
                                name='preferredDaysList'
                                title='Preferred Days'
                                lookupKey="PreferredDaysOptions"
                                isMulti
                                closeMenuOnSelect={false}
                                includeSelectAll={true}
                                includeAdditional={['No Preference']}
                                excludeFromAll={['No Preference']}
                                disabled={readOnly}
                                readOnlyMultiValueDisplay={displayDayList}
                                sort={sortDays}
                            />
                        </Cell>
                        <Cell small={6} large={6}>
                            <LookupSelectFieldGroup
                                name='preferredTime'
                                title='Preferred Time'
                                lookupKey="PreferredTimeOptions"
                                includeBlank={false}
                                disabled={readOnly}
                            />
                        </Cell>
                        <Cell small={4} large={2}>
                            <InputFormGroup type='number' name="frequencyHoursPerVisit" title='Units' disabled={readOnly} min={0} />
                        </Cell>
                        <Cell small={8} large={6}>
                            <Grid className='frequency-custom-style'>
                                <Cell small={3} large={2}>
                                    &nbsp;per&nbsp;
                                </Cell>
                                <Cell small={3} large={7}>
                                    <LookupSelectFieldGroup
                                        name='frequencyVisitsTimeframe'
                                        lookupKey="TimeFrame"
                                        includeBlank={false}
                                        disabled={readOnly}
                                        noLabel
                                        noError
                                    />
                                </Cell>
                                <Cell small={3} large={4}>
                                    <ErrorMessage name="frequencyVisitsTimeframe" />
                                </Cell>
                            </Grid>
                        </Cell>
                        <Cell small={8} large={8} className='special-considerations-list'>
                            <LookupCheckboxFieldGroup
                                name='specialConsiderationsList'
                                title='Special Considerations'
                                lookupKey="ServiceSpecialConsiderations"
                                disabled={readOnly}
                            />
                        </Cell>
                    </Grid>
                    <FocusOnFirstError />

                    {
                        !readOnly
                            ? <>
                                <SubmitMessage />

                                <div className='right form-buttons'>
                                    <SubmitButton
                                        notHTMLSubmit
                                        text={form.values.status === 'Active' && selectedService?.status !== form.values.status && !notification ? 'Save and Post to Providers' : 'Save'}
                                        onClick={handleButton(form.values.status === 'Active' && selectedService?.status !== form.values.status && !notification, form)}
                                    />

                                    {
                                        (user.hasAnyRole(Roles.WaiverServiceLeader, Roles.MarketAdmin) || (user.hasAnyRole(Roles.WaiverServiceCoordinator) && user.user.userID === selectedService?.createdBy))
                                        && !notification
                                        && !(isNew && form.values.status === 'Active')
                                        && !(!isNew && form.values.status === 'Active')
                                        && <button type='button' className='button secondary' disabled={form.isSubmitting} onClick={handleButton(true, form)}>
                                            Activate Posting for Providers
                                        </button>
                                    }

                                    {
                                        notification
                                            ? (
                                                <button className='button secondary' disabled={true}>
                                                    {
                                                        notification.createdOn
                                                            ? `Notifications queued on ${displayDate(notification.createdOn) || ''}`
                                                            : 'Notifications Queued'
                                                    }
                                                </button>
                                            )
                                            : null
                                    }
                                </div>
                            </>
                            : null
                    }
                </Form>
            )}
        </Formik>
    )
}

export default ServiceSection
